/**
 * @author Muhammad Omran
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2020-11-12
 * @description Implementation of the filter tabs widget
 * @filename filters-widget.tsx
 */

import React from "react";
import $ from "jquery";
import { connect } from "react-redux";
import { ComponentPropertyValue } from "@radivision/graphql/lib/ts/graphql/component-property-value";
import { SVG } from "../../component-configuration/svgs";
import { forceVisible } from "react-lazyload";
import { AppState } from "../../redux";

/**
 * interface that defines the json props filter object
 *
 * @interface
 */
interface JsonFilters {
  /**
   * filter id
   *
   * @type {string}
   * @memberof JsonFilters
   */
  id: string;

  /**
   * filter label
   *
   * @type {string}
   * @memberof JsonFilters
   */
  label: string;
}

/**
 * interface that defines the props of the component
 *
 * @interface
 */
interface FiltersWidgetProps extends ComponentPropertyValue {
  /**
   * align filter items
   *
   * @type {"left" | "center"}
   * @memberof FiltersWidgetProps
   */
  alignItems?: "left" | "center";

  /**
   * custom filter handler
   *
   * @type {(label: string) => void}
   * @memberof FiltersWidgetProps
   */
  customFilterHandler?: (label: string) => void;

  /**
   * flag to disable sticky feature on scroll
   *
   * @type {boolean}
   * @memberof FiltersWidgetProps
   */
  disableStickey?: boolean;

  /**
   * flag to hide all button
   *
   * @type {boolean}
   * @memberof FiltersWidgetProps
   */
  hideAll?: boolean;

  /**
   * remove the negative top margin that was added to elevate on top of hero component
   *
   * @type {boolean}
   * @memberof FiltersWidgetProps
   */
  resetTopMargin?: boolean;
  pageYOffset: number;
}

/**
 * interface that defines the state of the component
 *
 * @interface
 */
interface FiltersWidgetState {
  /**
   * current selected filter tab
   *
   * @type {string}
   * @memberof FeaturedItemPanelState
   */
  currentFilter: string;

  /**
   * teatured filtering tabs
   *
   * @type {JsonFilters[]}
   * @memberof FeaturedItemPanelProps
   */
  filters: JsonFilters[];
}

export class Component extends React.Component<FiltersWidgetProps, FiltersWidgetState> {
  constructor(props: FiltersWidgetProps) {
    super(props);
    const filters: JsonFilters[] = this.props.json && this.props.json.filters ? this.props.json.filters : [];
    this.appOnScroll = null;
    this.state = {
      filters,
      currentFilter: props.hideAll ? (filters.length ? filters[0].label.toLowerCase() : "") : "all",
    };
    this.includeItemOnFilter = this.includeItemOnFilter.bind(this);
  }

  detectMob() {
    const toMatch = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i];
    return toMatch.some((toMatchItem) => {
      return navigator.userAgent.match(toMatchItem);
    });
  }

  componentDidMount() {
    // detect browser device > if it's mobile add class to html document body
    if (this.detectMob()) {
      document.body.classList.add("mobile-browser");
    }
  }

  /**
   * Invoked when component finished re-render after an update
   */
  componentDidUpdate(prevProps: ComponentPropertyValue, prevState: FiltersWidgetState): void {
    if (prevState.currentFilter !== this.state.currentFilter && !this.props.customFilterHandler) {
      this.includeItemOnFilter();
    }
    // if user scrolls to 60px from the top of the left div
    if (this.state.filters && !this.props.disableStickey) {
      const filtersWidget = document.getElementById("filters-widget");
      const navbar = document.querySelector(".main-navbar");
      const filtersWidgetTopOffset = filtersWidget.offsetTop - navbar.clientHeight;
      if (this.props.pageYOffset >= filtersWidgetTopOffset + 10) {
        // stick the div
        filtersWidget.className = "featured-filter-tabs stickey";
        filtersWidget.style.top = `${navbar.clientHeight}px`;
      } else {
        // release the div
        filtersWidget.className = "featured-filter-tabs";
        filtersWidget.style.top = `auto`;
      }
    }
  }

  /**
   * to deciede to include the item after filtering or not
   */
  includeItemOnFilter() {
    $(".featured-filter-tabs ~ div.first-found").removeClass("first-found");
    if (this.state.currentFilter === "all") {
      $("div.carousel-panel").fadeIn(300);
    } else {
      let firstFound: boolean = false;
      $("div.carousel-panel").map((index, el) => {
        const elementTags: string[] = $(el)
          .data("tags")
          .split(",");
        let match: boolean = false;
        for (const filter of elementTags) {
          if (
            filter &&
            (filter.toLowerCase() === this.state.currentFilter.toLowerCase() ||
              filter
                .trim()
                .toLowerCase()
                .replace(/[\s]+/g, "-")
                .includes(
                  this.state.currentFilter
                    .trim()
                    .toLowerCase()
                    .replace(/[\s]+/g, "-"),
                ) ||
              this.state.currentFilter
                .trim()
                .toLowerCase()
                .replace(/[\s]+/g, "-")
                .includes(
                  filter
                    .trim()
                    .toLowerCase()
                    .replace(/[\s]+/g, "-"),
                ))
          ) {
            match = true;
            break;
          }
        }
        if (match) {
          if (!firstFound) {
            $(el).addClass("first-found");
            firstFound = true;
          }
          $(el).fadeIn(300);
        } else {
          $(el).fadeOut(300);
        }
      });
    }
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return this.state.filters ? (
      <div
        id="filters-widget"
        className={`featured-filter-tabs${this.props.resetTopMargin ? " mt-0" : ""}${
          this.props.alignItems && this.props.alignItems === "left" ? " justify-content-start" : ""
        }`}
      >
        <div className={`${this.props.alignItems && this.props.alignItems === "left" ? "justify-content-start" : ""}`}>
          {!this.props.hideAll ? (
            <span
              key={`filter-item-0`}
              className={`filter-item${this.state.currentFilter === "all" ? " active" : ""}`}
              onClick={() => {
                forceVisible();
                this.setState({ currentFilter: "all" });
                if (this.props.customFilterHandler) {
                  this.props.customFilterHandler("all");
                }
              }}
            >
              All
            </span>
          ) : null}
          {this.state.filters.map((f: JsonFilters, i: number) => (
            <span
              key={`filter-item-${i + 1}`}
              className={`filter-item${f.label.toLowerCase().includes("on topic") ? " with-icon" : ""}${
                this.state.currentFilter === f.label.toLowerCase() ? " active" : ""
              }`}
              onClick={() => {
                forceVisible();
                this.setState({ currentFilter: f.label.toLowerCase() });
                if (this.props.customFilterHandler) {
                  this.props.customFilterHandler(f.label);
                }
              }}
            >
              {f.label.toLowerCase().includes("on topic") ? (
                <i className="rdv-star-icon">{SVG.radivisionStar}</i>
              ) : null}
              {f.label}
            </span>
          ))}
        </div>
      </div>
    ) : null;
  }
}
const mapState = (state: AppState) => ({
  pageYOffset: state.app.pageYOffset,
});
export const FiltersWidget = connect(mapState)(Component);
